/**
 * Analytics Context Provider
 * Manages PostHog initialization and provides analytics functionality throughout the app
 */

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { isPostHogLoaded, getPostHog } from '../lib/analytics';
import { setupPageExitTracking, getPageTracker } from '../utils/pageTracking';
import { useAuth } from '../hooks/useAuth'; // Assuming you have this hook
import type { UseAnalyticsReturn } from '../lib/analyticsTypes';
import { useAnalytics } from '../hooks/useAnalytics';

interface AnalyticsContextType extends UseAnalyticsReturn {
  isInitialized: boolean;
  isPageTrackingEnabled: boolean;
  initializationError: string | null;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
  enablePageTracking?: boolean;
  enableSessionRecording?: boolean;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  enablePageTracking = true,
  enableSessionRecording = true
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [isPageTrackingEnabled, setIsPageTrackingEnabled] = useState(false);
  
  // Destructure analytics functions to get stable references
  const { 
    trackEvent, 
    trackButtonClick, 
    trackError, 
    trackPageView, 
    identify, 
    reset,
    isLoaded: analyticsIsLoaded 
  } = useAnalytics();
  const { user, isAuthenticated } = useAuth(); // Get user from your auth context

  // Wait for PostHog (initialized via PostHogProvider) to be ready
  useEffect(() => {
    let cancelled = false;
    const waitForPosthog = async () => {
      try {
        let attempts = 0;
        const maxAttempts = 50; // ~5s
        while (!cancelled && attempts < maxAttempts && !isPostHogLoaded()) {
          await new Promise(res => setTimeout(res, 100));
          attempts++;
        }
        if (!cancelled && isPostHogLoaded()) {
          setIsInitialized(true);
          console.log('✅ PostHog ready');
          if (enablePageTracking) {
            try {
              setupPageExitTracking();
              setIsPageTrackingEnabled(true);
              console.log('✅ Page tracking enabled');
            } catch (error) {
              console.error('❌ Failed to setup page tracking:', error);
            }
          }
        }
      } catch (error) {
        console.error('❌ Analytics readiness check failed:', error);
      }
    };
    waitForPosthog();
    return () => { cancelled = true; };
  }, [enablePageTracking]);

  // Identify user when authenticated
  useEffect(() => {
    if (isInitialized && isAuthenticated() && user) {
      try {
        identify(user.id.toString(), {
          email: user.email,
          // Add safe property access with fallbacks
          ...(user as any).name && { name: (user as any).name },
          ...(user as any).created_at && { created_at: (user as any).created_at },
          subscription_plan: (user as any).subscription_plan || 'free',
          onboarding_completed: (user as any).onboarding_completed || false,
          total_documents: (user as any).total_documents || 0,
          total_chats: (user as any).total_chats || 0
        });
        
        console.log('✅ User identified in analytics:', user.id);
      } catch (error) {
        console.error('❌ Failed to identify user:', error);
      }
    }
  }, [isInitialized, user, isAuthenticated, identify]);

  // Reset analytics on logout
  useEffect(() => {
    if (isInitialized && !isAuthenticated()) {
      try {
        reset();
        console.log('🔄 Analytics reset after logout');
      } catch (error) {
        console.error('❌ Failed to reset analytics:', error);
      }
    }
  }, [isInitialized, isAuthenticated, reset]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const pageTracker = getPageTracker();
      if (pageTracker) {
        pageTracker.destroy();
      }
    };
  }, []);

  const contextValue: AnalyticsContextType = useMemo(() => ({
    trackEvent,
    trackButtonClick,
    trackError,
    trackPageView,
    identify,
    reset,
    isLoaded: analyticsIsLoaded,
    isInitialized,
    isPageTrackingEnabled,
    initializationError
  }), [
    trackEvent,
    trackButtonClick,
    trackError,
    trackPageView,
    identify,
    reset,
    analyticsIsLoaded,
    isInitialized,
    isPageTrackingEnabled,
    initializationError
  ]);

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom hook to use analytics context
export const useAnalyticsContext = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  
  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  
  return context;
};

// HOC for automatic component tracking
export const withAnalytics = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string
) => {
  const WithAnalyticsComponent = (props: P) => {
    const { trackEvent, trackError } = useAnalyticsContext();

    useEffect(() => {
      // Track component mount
      trackEvent('component_viewed', {
        component_name: componentName,
        timestamp: new Date().toISOString()
      });
    }, [trackEvent]);

    // Error boundary functionality
    useEffect(() => {
      const handleError = (error: ErrorEvent) => {
        if (error.filename?.includes(componentName.toLowerCase())) {
          trackError(new Error(error.message), componentName);
        }
      };

      window.addEventListener('error', handleError);
      return () => window.removeEventListener('error', handleError);
    }, [trackError]);

    return <WrappedComponent {...props} />;
  };

  WithAnalyticsComponent.displayName = `withAnalytics(${componentName})`;
  return WithAnalyticsComponent;
};

// Hook for tracking component lifecycle
export const useComponentTracking = (componentName: string) => {
  const { trackEvent } = useAnalyticsContext();

  useEffect(() => {
    const startTime = Date.now();

    trackEvent('component_mounted', {
      component_name: componentName,
      timestamp: new Date().toISOString()
    });

    return () => {
      const timeSpent = Date.now() - startTime;
      trackEvent('component_unmounted', {
        component_name: componentName,
        time_spent_ms: timeSpent,
        timestamp: new Date().toISOString()
      });
    };
  }, [trackEvent, componentName]);
};

// Hook for tracking user interactions within a component
export const useInteractionTracking = (componentName: string) => {
  const { trackEvent } = useAnalyticsContext();

  const trackInteraction = useCallback((
    interactionType: string,
    details?: Record<string, any>
  ) => {
    trackEvent('user_interaction', {
      component_name: componentName,
      interaction_type: interactionType,
      ...details,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent, componentName]);

  return { trackInteraction };
};

// Provider for feature flag context
export const useFeatureFlags = () => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const { isInitialized } = useAnalyticsContext();

  useEffect(() => {
    if (isInitialized && typeof window !== 'undefined') {
      // Get all feature flags from the imported posthog instance
      const posthog = getPostHog();
      
      if (posthog && posthog.getAllFlags) {
        const allFlags = posthog.getAllFlags();
        setFlags(allFlags || {});
      }

      // Listen for flag changes
      const handleFlagChange = () => {
        if (posthog && posthog.getAllFlags) {
          setFlags(posthog.getAllFlags() || {});
        }
      };

      // PostHog doesn't have a built-in flag change listener, 
      // so we'll poll periodically or you can implement your own mechanism
      const interval = setInterval(handleFlagChange, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    }
  }, [isInitialized]);

  const isFeatureEnabled = useCallback((flagName: string, defaultValue: boolean = false): boolean => {
    if (!isInitialized || typeof window === 'undefined') {
      return defaultValue;
    }

    const posthog = getPostHog();
    return flags[flagName] ?? posthog?.isFeatureEnabled?.(flagName) ?? defaultValue;
  }, [flags, isInitialized]);

  return { flags, isFeatureEnabled };
};
